/**
 * Waldbrand — public, kid- & layperson-friendly wildfire information & monitor.
 *
 * Route: /waldbrand (PUBLIC, no auth)
 *
 * Why a dedicated page?
 *   The Pulse dashboard shows a small wildfire tile. The user feedback was:
 *   people want to *understand* what they see — what does "FRP" mean,
 *   what should I do when smoke is visible, how do I prevent fires myself.
 *   This page is the deep dive: live counts + glossary + behavioural
 *   guidance + prevention tips.
 *
 * Data sources
 *   GET /api/pulse/now            → quick live counts (24h totals, risk level)
 *   GET /api/pulse/wildfires      → top European hot-spots (lat/lon/FRP)
 *   Static behavioural / prevention copy (FAQ-style sections).
 */
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Fire, Warning, Phone, Wind, Drop, Leaf,
  ShieldCheck, Lightning, MapPin, ArrowRight, Sparkle, FireSimple,
  Cigarette, Car, Tree, Smiley, ClockCounterClockwise,
} from "@phosphor-icons/react";
import GlossaryTerm from "@/components/GlossaryTerm";
import { ExplainPanel, StoryCard } from "@/components/Explain";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const RISK_TONE = {
  hoch:    { color: "#FF6A5A", bg: "rgba(255,85,0,0.10)", label: "Hoch" },
  erhöht:  { color: "#FFC24B", bg: "#241C06", label: "Erhöht" },
  niedrig: { color: "#00FA9A", bg: "rgba(0,250,154,0.12)", label: "Niedrig" },
};

const WEEKDAYS_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
function fmtDayShort(iso) {
  const d = new Date(`${iso}T12:00:00Z`);
  return `${WEEKDAYS_DE[d.getUTCDay()]} ${d.getUTCDate()}.${d.getUTCMonth() + 1}.`;
}

function StatBig({ value, label, accent }) {
  return (
    <div className="border-l-4 pl-4 sm:pl-5" style={{ borderColor: accent }}>
      <div className="bb-metric text-4xl sm:text-5xl text-white tracking-tighter leading-none">
        {value ?? "—"}
      </div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-2">{label}</div>
    </div>
  );
}

function BehaviourCard({ icon: Icon, title, body, accent = "#00FA9A" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="bg-[#121212] border border-white/15 p-5 flex gap-4"
    >
      <div
        className="shrink-0 w-12 h-12 rounded-sm flex items-center justify-center"
        style={{ background: accent, color: "#FFFFFF" }}
      >
        <Icon size={22} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-base leading-tight">{title}</div>
        <p className="text-sm text-white/75 mt-1 leading-relaxed">{body}</p>
      </div>
    </motion.div>
  );
}

export default function Waldbrand() {
  const [pulse, setPulse] = useState(null);
  const [fires, setFires] = useState(null);
  const [week, setWeek] = useState(null);
  const [dayIdx, setDayIdx] = useState(0);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersLayer = useRef(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/api/pulse/now`).then((r) => mounted && setPulse(r.data)).catch(() => {});
    axios.get(`${API}/api/pulse/wildfires`).then((r) => mounted && setFires(r.data)).catch(() => {});
    axios.get(`${API}/api/pulse/wildfires/week`).then((r) => {
      if (!mounted || !r.data?.days?.length) return;
      setWeek(r.data);
      setDayIdx(r.data.days.length - 1);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Init Leaflet map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (containerRef.current._leaflet_id) delete containerRef.current._leaflet_id;
    const map = L.map(containerRef.current, {
      center: [50, 12], zoom: 4, scrollWheelZoom: true,
    });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: "© OpenStreetMap",
    }).addTo(map);
    markersLayer.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => { try { map.remove(); } catch { /* ignore */ } mapRef.current = null; };
  }, []);

  // Plot hot-spots (selected slider day, else latest 24h snapshot)
  useEffect(() => {
    if (!mapRef.current || !markersLayer.current) return;
    const pts = week?.days?.[dayIdx]?.points || fires?.europe_points;
    if (!pts) return;
    markersLayer.current.clearLayers();
    pts.slice(0, 200).forEach((hs) => {
      const size = Math.min(14, 4 + (hs.frp || 0) / 6);
      const c = (hs.conf || "").toLowerCase();
      const color =
        c === "h" || c === "high" ? "#FF6A5A" :
        c === "n" || c === "nominal" ? "#FFC24B" :
        "#D9A87C";
      L.circleMarker([hs.lat, hs.lon], {
        radius: size,
        color, fillColor: color, fillOpacity: 0.55, weight: 1,
      }).bindPopup(
        `<strong>FRP ${hs.frp?.toFixed?.(1) ?? "?"} MW</strong><br/>` +
        `Konfidenz: ${hs.conf || "?"}<br/>` +
        `${hs.lat.toFixed(2)}, ${hs.lon.toFixed(2)}`
      ).addTo(markersLayer.current);
    });
  }, [fires, week, dayIdx]);

  const wf = pulse?.wildfires;
  const risk = RISK_TONE[wf?.risk_level] || RISK_TONE.niedrig;
  const activeDay = week?.days?.[dayIdx] || null;
  const isLatestDay = !week || dayIdx === week.days.length - 1;
  const mapCount = activeDay ? activeDay.count : (fires?.europe_points?.length || 0);

  return (
    <div className="space-y-8" data-testid="waldbrand-page">
      <Helmet>
        <title>Waldbrand · Live-Monitor & Verhalten · bodenblick.com</title>
        <meta name="description" content="Aktuelle Waldbrand-Lage in Europa, was du im Brandfall tun musst und wie du Brände vermeidest." />
      </Helmet>

      {/* Hero */}
      <header
        className="eid-card p-6 md:p-10 relative overflow-hidden"
        data-testid="waldbrand-hero"
        style={{
          background: "linear-gradient(135deg, #1A0E08 0%, #241C06 60%, #0A0A0A 100%)",
        }}
      >
        <div
          className="absolute -top-20 -right-20 w-96 h-96 opacity-30 pointer-events-none"
          style={{ background: "radial-gradient(circle, #D13C30, transparent 60%)" }}
        />
        <div className="relative">
          <div className="flex items-center gap-2">
            <Fire size={22} className="text-[#FF6A5A]" weight="fill" />
            <div className="eid-label">Waldbrand · Lage · Verhalten · Prävention</div>
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-6xl lg:text-7xl font-bold tracking-tighter max-w-4xl leading-[0.95] bb-accent-stripe">
            Wenn der Wald brennt — wisse, was zu tun ist.
          </h1>
          <p className="mt-5 text-base md:text-lg text-white/75 max-w-3xl leading-relaxed">
            Jeder Waldbrand zerstört über Jahre gewachsenes Leben.{" "}
            <strong>9 von 10 Bränden sind menschengemacht</strong> — also
            vermeidbar. Wir zeigen dir die aktuelle Lage in Europa, was im
            Ernstfall zu tun ist und wie du selbst zur Prävention beiträgst.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest" style={{ color: risk.color }}>
            <motion.span
              className="w-2 h-2 rounded-full"
              style={{ background: risk.color }}
              animate={{ scale: [1, 1.6, 1], opacity: [1, 0.4, 1] }}
              transition={{ repeat: Infinity, duration: 1.4 }}
            />
            Aktuelles europaweites Risiko: {risk.label}
          </div>
        </div>
      </header>

      {/* Live numbers */}
      <section
        className="border border-white/15 bg-[#121212] p-5 sm:p-8"
        data-testid="waldbrand-live"
      >
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="eid-label inline-flex items-center gap-1.5">
              <Sparkle size={11} weight="fill" /> Live · letzte 24 Stunden
            </div>
            <h2 className="font-display text-2xl sm:text-4xl font-bold tracking-tighter mt-1">
              Wie viele Brände gerade aktiv sind.
            </h2>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            NASA FIRMS · VIIRS S-NPP · 3 h Cache
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
          <StatBig value={wf?.global_24h?.toLocaleString("de-DE")} label="Welt" accent="#F5F5F5" />
          <StatBig value={wf?.europe_24h?.toLocaleString("de-DE")} label="Europa" accent="#FFC24B" />
          <StatBig value={wf?.deat_24h?.toLocaleString("de-DE")} label="DE / AT" accent="#FF6A5A" />
          <StatBig value={wf?.max_frp ? `${wf.max_frp.toFixed(0)} MW` : "—"} label="Stärkster Hot-Spot" accent="#D9A87C" />
        </div>

        <ExplainPanel
          title={`Was bedeuten diese Zahlen — und was sind „Hot-Spots"?`}
          accent="#FF6A5A"
          testid="waldbrand-explain-numbers"
          className="mt-6"
        >
          <p>
            <strong>Hot-Spot</strong> nennen wir jeden Punkt am Boden, an dem ein NASA-Satellit
            (<GlossaryTerm word="FIRMS">VIIRS</GlossaryTerm>) eine ungewöhnliche
            Hitzequelle erkennt — meist ein aktives Feuer, manchmal auch ein heißer
            Industrieofen oder ein Vulkan. Die Auflösung beträgt 375 m, deshalb wird ein
            großer Brand oft als mehrere Hot-Spots gezählt.
          </p>
          <p>
            <strong><GlossaryTerm word="FRP">FRP</GlossaryTerm> (Fire Radiative Power)</strong>{" "}
            misst die Strahlungsleistung in Megawatt. Je höher der Wert, desto intensiver
            das Feuer:
          </p>
          <ul className="ml-5 list-disc text-[13px] leading-relaxed">
            <li><strong>&lt; 20 MW</strong> — kleiner Brand, oft kontrollierbar (Schwelfeuer, Glutnester).</li>
            <li><strong>20-100 MW</strong> — ausgewachsener Wald-/Flurbrand, Einsatzkräfte rückten aus.</li>
            <li><strong>&gt; 100 MW</strong> — Großbrand mit Flammen über 5 m Höhe — Evakuierungen wahrscheinlich.</li>
          </ul>
          <p className="text-[#FF7A45]">
            <strong>Warum sind die Zahlen wichtig?</strong> Steigt die Zahl in deiner Region
            stark an, ist die Brandgefahr für die nächsten Tage erhöht — Boden ist
            staubtrocken, Wind transportiert Funken weiter, jeder Funke kann fangen.
          </p>
        </ExplainPanel>
      </section>

      {/* Map */}
      <section data-testid="waldbrand-map-section">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="eid-label">Hot-Spot-Karte Europa</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tighter mt-0.5" data-testid="waldbrand-map-count">
              {mapCount.toLocaleString("de-DE")} Hot-Spots{" "}
              <span className="text-[#FF6A5A]">
                {activeDay ? `· ${isLatestDay ? "heute" : fmtDayShort(activeDay.date)}` : ""}
              </span>
            </h2>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            Punkt-Größe = FRP · Rot = hohe Konfidenz
          </span>
        </div>

        {/* Time-slider: 7-day fire history */}
        {week?.days?.length > 1 && (
          <div className="border border-white/15 bg-[#121212] p-4 sm:p-5 mb-3" data-testid="waldbrand-timeslider">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="eid-label inline-flex items-center gap-1.5">
                <ClockCounterClockwise size={11} weight="bold" /> Zeitreise · letzte 7 Tage
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#FF6A5A]" data-testid="waldbrand-timeslider-label">
                {fmtDayShort(week.days[dayIdx].date)} · {week.days[dayIdx].count.toLocaleString("de-DE")} Hot-Spots · max. {week.days[dayIdx].max_frp} MW
              </span>
            </div>
            <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${week.days.length}, 1fr)` }}>
              {week.days.map((d, i) => {
                const max = Math.max(...week.days.map((x) => x.count), 1);
                const h = Math.max(8, Math.round((d.count / max) * 56));
                const sel = i === dayIdx;
                return (
                  <button
                    key={d.date}
                    data-testid={`waldbrand-day-btn-${i}`}
                    onClick={() => setDayIdx(i)}
                    className={`flex flex-col items-center justify-end gap-1.5 pt-2 pb-2 border transition-colors ${
                      sel ? "border-[#FF5500]/60 bg-[#FF5500]/10" : "border-white/10 hover:border-white/25"
                    }`}
                  >
                    <span className="text-[10px] font-mono" style={{ color: sel ? "#FF6A5A" : "#71717A" }}>
                      {d.count.toLocaleString("de-DE")}
                    </span>
                    <span
                      className="w-3/5 rounded-sm transition-colors"
                      style={{ height: h, background: sel ? "#FF5500" : "rgba(255,255,255,0.18)" }}
                    />
                    <span className={`text-[9px] font-mono uppercase tracking-wider ${sel ? "text-white" : "text-zinc-500"}`}>
                      {fmtDayShort(d.date)}
                    </span>
                  </button>
                );
              })}
            </div>
            <input
              type="range"
              min={0}
              max={week.days.length - 1}
              value={dayIdx}
              onChange={(e) => setDayIdx(Number(e.target.value))}
              className="w-full mt-3 accent-[#FF5500]"
              aria-label="Tag auswählen"
              data-testid="waldbrand-day-slider"
            />
          </div>
        )}

        <div className="border border-white/15 bg-[#121212] overflow-hidden" data-testid="waldbrand-map">
          <div ref={containerRef} className="w-full h-[420px]" />
        </div>
      </section>

      <StoryCard
        eyebrow="So liest du diese Seite"
        title="Drei Ebenen — eine klare Reaktion."
        lead="Oben siehst du die aktuelle Lage in Echtzeit. In der Mitte erklären wir, was du im Notfall tun musst. Unten zeigen wir, wie du Brände gar nicht erst entstehen lässt."
        bullets={[
          "Lage = Hot-Spots & FRP-Werte aus NASA FIRMS Satellit",
          "Notfall = Verhalten in 5 Schritten · 112 / 122 / 144",
          "Prävention = 6 alltägliche Dinge, die jede:r vermeiden kann",
        ]}
        accent="#FF6A5A"
        icon={Fire}
        testid="waldbrand-story"
      />

      {/* Behaviour — what to do */}
      <section data-testid="waldbrand-behaviour">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="eid-label inline-flex items-center gap-1.5 text-[#FF6A5A]">
              <Warning size={11} weight="fill" /> Im Brandfall
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tighter mt-1 leading-[0.95]">
              Was tun, wenn du Feuer siehst?
            </h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <BehaviourCard
            icon={Phone}
            title="1. Sofort 112 (EU) oder 122 (DE) / 122 (AT) wählen."
            body="Standort möglichst genau angeben: Wegmarken, Wegweiser, GPS-Pin teilen. Nicht auflegen, bis die Leitstelle bestätigt."
            accent="#FF6A5A"
          />
          <BehaviourCard
            icon={Wind}
            title="2. Mit dem Wind fliehen, nicht gegen ihn."
            body="Quer zum Wind oder gegen den Hang flüchten — Feuer rast bergauf doppelt so schnell. Tief gehen, da Rauch oben am dichtesten ist."
            accent="#FFC24B"
          />
          <BehaviourCard
            icon={Drop}
            title="3. Nasses Tuch vor Nase und Mund."
            body="Schon 5 Atemzüge dichten Rauchs können bewusstlos machen. Falls Wasser fehlt: Urin tut's auch. Wichtig: feucht, nicht trocken."
            accent="#8B7CF6"
          />
          <BehaviourCard
            icon={Smiley}
            title="4. Andere warnen — vor allem Kinder & Tiere."
            body="Schreien, Hupen, Telegram-/SMS-Kette starten. Tiere lassen sich oft mit lockendem Ruf in Sicherheit bringen — Käfige geöffnet, nicht angefasst."
            accent="#00FA9A"
          />
          <BehaviourCard
            icon={ShieldCheck}
            title="5. Wenn klein & frisch: Mit Erde, Sand oder Tuch ersticken."
            body="Nur wenn KEINE Stichflamme da ist und Fluchtweg frei bleibt. Wasser nur, wenn es nicht in eine glühende Decke verdampft (das beschleunigt das Feuer)."
            accent="#D9A87C"
          />
          <BehaviourCard
            icon={Car}
            title="6. Auto verlassen, wenn die Straße brennt."
            body="Tanks explodieren bei > 600 °C. Fenster zu, Lüftung aus, Motor aus, raus und gegen den Wind weg. Auf KEINEN Fall durch die Flammen fahren."
            accent="#F5F5F5"
          />
        </div>
      </section>

      {/* Prevention */}
      <section data-testid="waldbrand-prevention">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="eid-label inline-flex items-center gap-1.5 text-[#00FA9A]">
              <ShieldCheck size={11} weight="fill" /> Prävention
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tighter mt-1 leading-[0.95]">
              Was du selbst tun kannst.
            </h2>
            <p className="text-sm text-white/70 mt-2 max-w-2xl">
              Studien des österreichischen BFW zeigen: <strong>87 % aller Waldbrände</strong> in
              Mitteleuropa entstehen durch menschliche Unachtsamkeit. Diese 6 Punkte verhindern
              fast alle davon.
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <BehaviourCard
            icon={Cigarette}
            title="Kippen niemals in den Wald — niemals."
            body="Eine glimmende Zigarette hat 700 °C. Trockenes Laub fängt bei 300 °C. Auch nasses Laub trocknet in Minuten in der Sonne."
            accent="#FF6A5A"
          />
          <BehaviourCard
            icon={Fire}
            title="Lagerfeuer nur auf gekennzeichneten Plätzen."
            body={`In Trockenperioden (April-Oktober) sind offene Feuer oft per Verordnung verboten. Such-Tool: „Brandschutzverordnung [Bundesland]".`}
            accent="#FFC24B"
          />
          <BehaviourCard
            icon={Car}
            title="Auto NIE auf trockenem Gras parken."
            body="Heißer Katalysator (> 400 °C) entzündet hohes Gras in 30 Sekunden. Immer auf Schotter/Asphalt, nie auf Sommerwiese."
            accent="#D9A87C"
          />
          <BehaviourCard
            icon={Lightning}
            title="Glasflaschen aufsammeln — auch fremde."
            body="Sonnenstrahlen, gebündelt durch eine liegende Flasche, zünden trockenes Laub binnen Minuten an. Ein einziger Brennpunkt reicht."
            accent="#8B7CF6"
          />
          <BehaviourCard
            icon={Tree}
            title="Bäume vital halten — auf eigenem Grund."
            body="Mischwald statt Fichtenmonokultur. Totholz raus, Kronenabstand vergrößern. Vitaler Wald hat höhere Brand-Resistenz."
            accent="#00FA9A"
          />
          <BehaviourCard
            icon={FireSimple}
            title="Stoppelfeuer? Nur kontrolliert & angemeldet."
            body="Brennen von Stoppelfeldern ist in DE & AT mehrheitlich verboten. Wo erlaubt: Wind beobachten, Feuerwehr & Nachbarn vorher informieren, Wasser bereitstellen."
            accent="#FF5500"
          />
        </div>
      </section>

      {/* CTA */}
      <section
        className="border border-white/15 bg-[#F5F5F5] text-[#0A0A0A] p-6 sm:p-10"
        data-testid="waldbrand-cta"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <Leaf size={32} weight="fill" className="text-[#FFC24B] shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-2xl sm:text-4xl font-bold tracking-tighter leading-tight">
              Tägliche Bodenfeuchte für deine Region.
            </h3>
            <p className="mt-2 text-[#0A0A0A]/70 leading-relaxed max-w-2xl">
              Trockene Böden brennen schneller. In {"„Entdecken"} siehst du, wie viel
              Wasser im Oberboden deiner Region steckt — und ob jetzt ein Risiko besteht.
            </p>
            <Link
              to="/entdecken"
              className="mt-4 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-3 text-sm font-bold uppercase tracking-widest hover:bg-[#FF5500] transition-colors"
              data-testid="waldbrand-cta-entdecken"
            >
              Bodenfeuchte ansehen <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <p className="text-xs font-mono text-zinc-400 text-center">
        Datenquelle: <a className="underline" href="https://firms.modaps.eosdis.nasa.gov/" target="_blank" rel="noreferrer">NASA FIRMS</a> · VIIRS Suomi-NPP · aktualisiert alle 3 Stunden
        <span className="mx-2">·</span>
        <Link to="/glossar" className="hover:underline inline-flex items-center gap-1">
          <MapPin size={10} /> Glossar
        </Link>
      </p>
    </div>
  );
}
